import { Deferred, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { BrandLogo } from "@/components/brand-logo"
import {
  CrewRosterSkeleton,
  ThisWeekLeadersSkeleton,
} from "@/components/creator/crew-hub-skeletons"
import { CrewPulseSection } from "@/components/creator/crew-pulse-section"
import { CrewRosterGrid } from "@/components/creator/crew-roster-grid"
import { CrewStatRow } from "@/components/creator/crew-stat-row"
import { PulseRowSkeleton } from "@/components/creator/pulse-row"
import { ThisWeekLeadersSection } from "@/components/creator/this-week-leaders-section"
import { Card, CardContent } from "@/components/ui/card"
import { useCurrentContext } from "@/hooks/use-current-context"
import AppLayout from "@/layouts/app-layout"
import { MOTION_BEATS } from "@/lib/motion"
import type { CrewPulse } from "@/types/collab"
import type { CrewRoster, CrewStats, ThisWeekLeaders } from "@/types/crew-hub"

interface CrewIndexProps {
  /** Immediate — social-proof stat row (crew size + coins given). */
  crew_stats: CrewStats
  /** Deferred (Inertia) — absent on first paint (skeleton), then `{events}`. */
  crew_pulse?: CrewPulse
  /** Deferred — top-3 + self-pin + climb. */
  this_week_leaders?: ThisWeekLeaders
  /** Deferred — teammate roster grid. */
  crew_roster?: CrewRoster
}

/**
 * OKS-474 — §18 Crew tab social hub (`/my-crew` → `Creator/Crew/Index`).
 *
 * Extends the shipped §06 page (OKS-473) with three sections BELOW the
 * header + Pulse, all as white cards on the grey page (no restructure):
 * social-proof stat row → Crew Pulse → This Week's Leaders → Your Crew.
 *
 * `crew_stats` is immediate so the stat row paints with the chrome; the
 * heavier reads (`crew_pulse`, `this_week_leaders`, `crew_roster`) are
 * deferred and stream in behind skeletons (UX Spec §3). The header subtitle
 * is fed by the deferred leaders prop — a muted "Loading your crew…" while it
 * resolves, then "you're #X this week" or the "📈 YOU CLIMBED" banner.
 *
 * Empty crew (`crew_size ≤ 1` — effectively just the viewer) collapses to a
 * single "you're early" card; stats/leaders/roster are hidden (PRD Q6).
 */
export default function CrewIndex({
  crew_stats,
  crew_pulse,
  this_week_leaders,
  crew_roster,
}: CrewIndexProps) {
  const { t } = useTranslation()
  const { currentBrand } = useCurrentContext()
  const isEmpty = crew_stats.crew_size <= 1

  // The shipped §06 Crew Pulse card renders in BOTH states. §18's "empty crew"
  // hides only the §18 ADDITIONS (stat row, leaders, roster) — the binding
  // PRD §1/§5 + UX §3 enumerate exactly those three as hidden — while keeping
  // the §06 base (header + Pulse) intact + a "you're early" note. Keeps the
  // shipped OKS-473 C3 pulse-empty contract green (KD-8).
  const pulseCard = (
    <Card className="overflow-hidden py-3">
      <CardContent className="px-0">
        <Deferred
          data="crew_pulse"
          fallback={
            <ul className="divide-border/60 flex flex-col divide-y px-0">
              {Array.from({ length: 4 }).map((_, i) => (
                <PulseRowSkeleton key={i} />
              ))}
            </ul>
          }
        >
          <CrewPulseSection events={crew_pulse?.events ?? []} />
        </Deferred>
      </CardContent>
    </Card>
  )

  return (
    <AppLayout>
      <Head title={t("crew_pulse.tab_title")} />

      <main className="mx-auto flex w-full max-w-2xl flex-col gap-5 py-5">
        <header className="flex items-start justify-between gap-3 px-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">
              {t("crew_pulse.tab_title")}
            </h1>
            {!isEmpty && (
              <Deferred
                data="this_week_leaders"
                fallback={
                  <p className="text-muted-foreground text-sm">
                    {t("crew_hub.loading_subtitle")}
                  </p>
                }
              >
                <CrewHeaderSubtitle data={this_week_leaders} />
              </Deferred>
            )}
          </div>
          <BrandLogo
            logoUrl={currentBrand?.logo_url ?? null}
            name={currentBrand?.name ?? ""}
            className="size-10 shrink-0 rounded-full"
            testId="crew-brand-logo"
          />
        </header>

        {isEmpty ? (
          <>
            <CrewEmptyCard brandName={currentBrand?.name ?? ""} />
            {pulseCard}
          </>
        ) : (
          <>
            <CrewStatRow stats={crew_stats} />

            {pulseCard}

            <Deferred
              data="this_week_leaders"
              fallback={<ThisWeekLeadersSkeleton />}
            >
              <DeferredLeaders data={this_week_leaders} />
            </Deferred>

            <Deferred data="crew_roster" fallback={<CrewRosterSkeleton />}>
              <DeferredRoster roster={crew_roster} />
            </Deferred>
          </>
        )}
      </main>
    </AppLayout>
  )
}

/**
 * Header subtitle (rank / climb), fed by the deferred leaders prop. Renders
 * the climb banner when the viewer improved week-over-week, else the plain
 * rank line, else nothing (unranked this week).
 */
function CrewHeaderSubtitle({ data }: { data?: ThisWeekLeaders }) {
  const { t } = useTranslation()
  if (!data) return null

  if (data.climb) {
    return (
      <p
        data-testid="crew-climb-banner"
        className={`text-primary text-sm font-semibold ${MOTION_BEATS["rank-up"]}`}
      >
        {t("crew_hub.climb_banner", {
          count: data.climb.spots,
          rank: data.climb.rank,
        })}
      </p>
    )
  }

  if (data.viewer_rank != null) {
    return (
      <p
        data-testid="crew-rank-subtitle"
        className="text-muted-foreground text-sm"
      >
        {t("crew_hub.subtitle_rank", { rank: data.viewer_rank })}
      </p>
    )
  }

  return null
}

/** Single subtle entrance on the leaders card (UX Spec §3/§4 — CSS-only). */
function DeferredLeaders({ data }: { data?: ThisWeekLeaders }) {
  if (!data) return null
  return (
    <div className={MOTION_BEATS["overlay-enter"]}>
      <ThisWeekLeadersSection data={data} />
    </div>
  )
}

function DeferredRoster({ roster }: { roster?: CrewRoster }) {
  if (!roster) return null
  return (
    <div className={MOTION_BEATS["overlay-enter"]}>
      <CrewRosterGrid roster={roster} />
    </div>
  )
}

/** "You're early to the crew" — premium empty state (PRD Q6 / UX Spec §3). */
function CrewEmptyCard({ brandName }: { brandName: string }) {
  const { t } = useTranslation()
  return (
    <Card
      data-testid="crew-empty-card"
      className={MOTION_BEATS["overlay-enter"]}
    >
      <CardContent className="flex flex-col items-center gap-2 px-6 py-10 text-center">
        <h2 className="text-base font-semibold">{t("crew_hub.empty.title")}</h2>
        <p className="text-muted-foreground max-w-sm text-sm">
          {t("crew_hub.empty.body", { brand: brandName })}
        </p>
      </CardContent>
    </Card>
  )
}
